import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { 
  Grid, 
  LinearProgress, 
  Typography, 
  Button, 
  Divider, 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import PieChartIcon from '@material-ui/icons/PieChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { has, get, isNull } from "lodash";
import api from "../../services/api";
import { formatCompanyDateTime, resolveCompanyTimezone } from "../../utils/companyTimezone";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Pie, Doughnut } from 'react-chartjs-2';

// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  textRight: {
    textAlign: "right",
  },
  tabPanelsContainer: {
    padding: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    paddingBottom: 0,
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  tableTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 500,
  },
  chartContainer: {
    height: 300,
    marginBottom: theme.spacing(4),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
  deliveredChip: {
    backgroundColor: theme.palette.success.main,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: theme.palette.error.main,
  },
  infoChip: {
    backgroundColor: theme.palette.info ? theme.palette.info.main : "#1976d2",
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  fullWidthGrid: {
    width: '100%',
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const CampaignReport = () => {
  const classes = useStyles();
  const history = useHistory();

  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [validContacts, setValidContacts] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [confirmationRequested, setConfirmationRequested] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageRows, setMessageRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [shippingPage, setShippingPage] = useState(1);
  const [totalShippingPages, setTotalShippingPages] = useState(1);
  const [uniqueNumbers, setUniqueNumbers] = useState(0);
  const [failedMessages, setFailedMessages] = useState(0);
  const [totalMessagesSent, setTotalMessagesSent] = useState(0);
  const [operationalStats, setOperationalStats] = useState({});
  const [selectedFailure, setSelectedFailure] = useState(null);
  const mounted = useRef(true);
  const realtimeRefreshInFlight = useRef(false);
  const lastRealtimeRefreshAt = useRef(0);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();
  const normalizeShippingStatus = (item) => {
    const raw = String(item?.status || "").toLowerCase();
    if (raw) {
      const legacyMap = {
        pendente: "pending",
        waiting: "pending",
        waiting_warmup: "waiting_warmup",
        waiting_connection: "waiting_connection",
        waiting_retry: "waiting_retry",
        processando: "processing",
        sending: "processing",
        enviado: "sent",
        enviada: "sent",
        entregue: "delivered",
        lido: "read",
        falhou: "failed",
        failed_temporary: "failed_temporary",
        failed_permanent: "failed",
        falhou_retryavel: "failed_retryable",
        cancelado: "cancelled",
      };
      return legacyMap[raw] || raw;
    }
    if (item?.readAt) return "read";
    if (item?.deliveredAt) return "delivered";
    if (item?.sentAt) return "sent";
    if (item?.failedAt) return "failed";
    return "pending";
  };

  const shippingStatusLabel = {
    pending: "Pendente",
    waiting_warmup: "Aguardando warmup",
    waiting_connection: "Aguardando conexão",
    waiting_retry: "Aguardando retry",
    processing: "Processando",
    sent: "Enviado",
    delivered: "Entregue",
    read: "Lido",
    played: "Reproduzido",
    failed_retryable: "Aguardando retry",
    failed_temporary: "Falha temporária",
    failed: "Falhou",
    cancelled: "Cancelado"
  };

  const formatRetryInfo = (attempts, nextRetryAt, status) => {
    const base = `Tentativas: ${attempts || 0}`;
    const normalized = normalizeShippingStatus({ status });
    const retryableStatuses = ["failed_retryable", "failed_temporary", "waiting_warmup", "waiting_connection", "waiting_retry"];
    if (!nextRetryAt || !retryableStatuses.includes(normalized)) {
      return base;
    }
    const retryDate = new Date(nextRetryAt);
    if (Number.isNaN(retryDate.getTime())) return `${base} | Próximo: -`;
    const diffMs = retryDate.getTime() - Date.now();
    const diffMin = Math.ceil(diffMs / 60000);
    const eta = diffMin > 0 ? `em ${diffMin} min` : "agora";
    return `${base} | Próximo: ${datetimeToClient(nextRetryAt)} (${eta})`;
  };
  
  // Dados para o gráfico de pizza
  const [chartData, setChartData] = useState({
    labels: ['Entregues', 'Aguardando', 'Falhas'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });
  
  // Opções para o gráfico de pizza
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error(i18n.t("campaignReport.toasts.noPermissionRedirect"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      findCampaign();
    }

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      if (has(campaign, "contactList") && has(campaign.contactList, "contacts")) {
        const contactList = get(campaign, "contactList");
        const valids = contactList.contacts.filter((c) => c.isWhatsappValid);
        setValidContacts(valids.length);
      } else if (has(campaign, "tagListId")) {
        // Se estamos usando tags em vez de listas de contato
        setValidContacts(delivered); // Usamos as entregas como contatos válidos
      }

      if (has(campaign, "shipping") && Array.isArray(campaign.shipping)) {
        const contacts = get(campaign, "shipping");
        const delivered = contacts.filter((c) => !isNull(c.deliveredAt));
        const confirmationRequested = contacts.filter(
          (c) => !isNull(c.confirmationRequestedAt)
        );
        const confirmed = contacts.filter(
          (c) => !isNull(c.deliveredAt) && !isNull(c.confirmationRequestedAt)
        );
        setDelivered(delivered.length);
        setConfirmationRequested(confirmationRequested.length);
        setConfirmed(confirmed.length);
      }
    }
  }, [campaign]);

  useEffect(() => {
    // Evitar divisão por zero
    if (validContacts > 0) {
      setPercent((delivered / validContacts) * 100);
    } else {
      setPercent(0);
    }
  }, [delivered, validContacts]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCampaignEvent = (data) => {
      if (data?.record?.id === +campaignId) {
        setCampaign(data.record);
        refreshRealtimeData();

        if (data.record.status === "FINALIZADA") {
          setTimeout(() => {
            findCampaign();
          }, 5000);
        }
      }
    };
    socket.on(`company-${companyId}-campaign`, onCampaignEvent);

    return () => {
      socket.off(`company-${companyId}-campaign`, onCampaignEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    const isActiveCampaign = ["PROGRAMADA", "EM_ANDAMENTO", "AGUARDANDO_WARMUP", "SUSPENSA_CONEXAO"].includes(campaign?.status);
    if (!isActiveCampaign) return undefined;

    const interval = setInterval(() => {
      refreshRealtimeData();
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.status, campaignId, rowsPerPage, page]);

  const fetchCampaignStats = async () => {
    try {
      const { data: stats } = await api.get(`/campaigns/${campaignId}/stats`);
      setDelivered(stats.deliveredMessages);
      setValidContacts(stats.totalMessages);
      setUniqueNumbers(stats.uniqueNumbers);
      setFailedMessages(stats.failedMessages);
      setTotalMessagesSent(stats.totalMessages);
      setOperationalStats(stats);

      setChartData({
        labels: ["Entregues", "Aguardando", "Falhas"],
        datasets: [
          {
            data: [stats.deliveredMessages, stats.pendingMessages, stats.failedMessages],
            backgroundColor: [
              "rgba(75, 192, 192, 0.7)",
              "rgba(255, 206, 86, 0.7)",
              "rgba(255, 99, 132, 0.7)",
            ],
            borderColor: [
              "rgba(75, 192, 192, 1)",
              "rgba(255, 206, 86, 1)",
              "rgba(255, 99, 132, 1)",
            ],
            borderWidth: 1,
          },
        ],
      });
    } catch (statsError) {
      console.error("Erro ao buscar estatísticas:", statsError);
    }
  };

  const fetchShippingPage = async (serverPage = 1, pageSize = rowsPerPage) => {
    try {
      const { data: shippingResponse } = await api.get(
        `/campaigns/${campaignId}/shipping?page=${serverPage}&pageSize=${pageSize}`
      );

      if (shippingResponse && shippingResponse.shipping) {
        const shippingData = shippingResponse.shipping;

        const formattedRows = shippingData.map(item => ({
          id: item.id,
          jobId: item.jobId,
          number: item.number,
          message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? "..." : "") : "",
          fullMessage: item.message || "",
          deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
          readAt: item.readAt ? datetimeToClient(item.readAt) : null,
          failedAt: item.failedAt ? datetimeToClient(item.failedAt) : null,
          sentAt: item.sentAt ? datetimeToClient(item.sentAt) : (item.createdAt ? datetimeToClient(item.createdAt) : null),
          attempts: item.attempts || 0,
          nextRetryAt: item.nextRetryAt || null,
          errorMessage: item.errorMessage || null,
          statusReason: item.statusReason || null,
          failureCategory: item.failureCategory || null,
          failureReason: item.failureReason || null,
          failureDetails: item.failureDetails || null,
          workerName: item.workerName || null,
          status: normalizeShippingStatus(item)
        }));

        setMessageRows(formattedRows);
        setTotalShippingPages(shippingResponse.totalPages || 1);
        setShippingPage(shippingResponse.currentPage || 1);
      } else {
        setMessageRows([]);
      }
    } catch (shippingError) {
      console.error("Erro ao buscar dados de envio:", shippingError);
    }
  };

  const refreshRealtimeData = async () => {
    const now = Date.now();
    if (realtimeRefreshInFlight.current) return;
    if (now - lastRealtimeRefreshAt.current < 1500) return;

    realtimeRefreshInFlight.current = true;
    lastRealtimeRefreshAt.current = now;

    try {
      await Promise.all([
        fetchCampaignStats(),
        fetchShippingPage(page + 1, rowsPerPage),
      ]);
    } finally {
      realtimeRefreshInFlight.current = false;
    }
  };

  const findCampaign = async () => {
    try {
      setLoading(true);
      
      // Buscar dados básicos da campanha (sem shipping para evitar estouro de memória)
      const { data: campaign } = await api.get(`/campaigns/${campaignId}`);
      setCampaign(campaign);
      
      await fetchCampaignStats();
      await fetchShippingPage(1, rowsPerPage);
      
    } catch (error) {
      console.error("Erro ao buscar campanha:", error);
      toast.error(i18n.t("campaignReport.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaignReport.inactive");
      case "PROGRAMADA":
        return i18n.t("campaignReport.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaignReport.process");
      case "SUSPENSA_CONEXAO":
        return i18n.t("campaignReport.suspendedConnection");
      case "AGUARDANDO_WARMUP":
        return "Aguardando warmup";
      case "CANCELADA":
        return i18n.t("campaignReport.cancelled");
      case "FINALIZADA":
        return i18n.t("campaignReport.finished");
      default:
        return val;
    }
  };
  
  // Manipuladores para a paginação da tabela
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Carregar dados com novo tamanho de página
    loadMoreShipping(1, parseInt(event.target.value, 10));
  };
  
  // Função para carregar mais dados de shipping
  const loadMoreShipping = async (newPage = 1, pageSize = 50) => {
    try {
      setLoading(true);
      await fetchShippingPage(newPage, pageSize);
    } catch (error) {
      console.error("Erro ao carregar mais dados de envio:", error);
      toast.error(i18n.t("campaignReport.toasts.shippingLoadError"));
      setMessageRows([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para formatar o número de telefone
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    // Verifica se é um ID de grupo (normalmente começa com números grandes)
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    // Formata número normal
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };
  
  // Função para obter o status do envio
  const getMessageStatusChip = (status) => {
    const normalized = normalizeShippingStatus({ status });
    switch (normalized) {
      case 'delivered':
        return (
          <Chip 
            label={i18n.t("campaignReport.shippingStatus.delivered")} 
            size="small" 
            icon={<DoneAllIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.deliveredChip}`} 
          />
        );
      case 'pending':
      case 'processing':
        return (
          <Chip 
            label={normalized === "processing" ? i18n.t("campaignReport.shippingStatus.processing") : i18n.t("campaignReport.shippingStatus.pending")} 
            size="small" 
            icon={<ScheduleIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.pendingChip}`} 
          />
        );
      case 'sent':
        return (
          <Chip 
            label={i18n.t("campaignReport.shippingStatus.sent")} 
            size="small" 
            icon={<DoneIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.pendingChip}`} 
          />
        );
      case 'read':
      case 'played':
        return (
          <Chip 
            label={normalized === "read" ? i18n.t("campaignReport.shippingStatus.read") : i18n.t("campaignReport.shippingStatus.played")} 
            size="small" 
            icon={<DoneAllIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.deliveredChip}`} 
          />
        );
      case 'failed':
      case 'failed_retryable':
      case 'failed_temporary':
        return (
          <Chip 
            label={shippingStatusLabel[normalized] || i18n.t("campaignReport.shippingStatus.failed")} 
            size="small" 
            icon={<ErrorIcon fontSize="small" />} 
            className={`${classes.statusChip} ${normalized === "failed" ? classes.failedChip : classes.pendingChip}`} 
          />
        );
      case 'waiting_warmup':
      case 'waiting_connection':
      case 'waiting_retry':
        return (
          <Chip
            label={shippingStatusLabel[normalized]}
            size="small"
            icon={<ScheduleIcon fontSize="small" />}
            className={`${classes.statusChip} ${classes.infoChip}`}
          />
        );
      case 'cancelled':
        return (
          <Chip 
            label={i18n.t("campaignReport.shippingStatus.cancelled")}
            size="small"
            icon={<ErrorIcon fontSize="small" />}
            variant="outlined"
          />
        );
      default:
        return (
          <Chip 
            label={shippingStatusLabel[normalized] || i18n.t("campaignReport.shippingStatus.pending")} 
            size="small" 
            variant="outlined" 
          />
        );
    }
  };

  const parseFailureDetails = (value) => {
    if (!value) return null;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
      return value;
    }
  };

  // Função para exportar dados para Excel
  const exportToExcel = async () => {
    try {
      toast.info("Gerando relatório Excel, aguarde...");
      const companyTimezone = resolveCompanyTimezone(
        user?.company?.timezone || localStorage.getItem("companyTimezone")
      );
      
      // Buscar todos os dados de shipping em lotes (máximo 1000 por página)
      let allShippingData = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000; // Limite máximo do backend
      
      while (hasMoreData) {
        const { data: shippingResponse } = await api.get(`/campaigns/${campaignId}/shipping?page=${currentPage}&pageSize=${pageSize}`);
        
        if (!shippingResponse || !shippingResponse.shipping) {
          break;
        }
        
        allShippingData = [...allShippingData, ...shippingResponse.shipping];
        
        // Verificar se há mais páginas
        hasMoreData = currentPage < shippingResponse.totalPages;
        currentPage++;
        
        // Atualizar progresso
        toast.info(`Carregando dados... ${allShippingData.length} registros encontrados`);
      }
      
      if (allShippingData.length === 0) {
        toast.error("Nenhum dado encontrado para exportar");
        return;
      }

      const shippingData = allShippingData;
      
      // Preparar dados para Excel
      const excelData = [
        // Cabeçalho
        ["Relatório de Campanha", "", "", "", "", "", "", "", ""],
        [`Campanha: ${campaign.name}`, "", "", "", "", "", "", "", ""],
        [`Data de Geração: ${formatCompanyDateTime(new Date().toISOString(), companyTimezone)}`, "", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        // Cabeçalhos das colunas
        ["ID", "Destinatário", "Mensagem", "Enviado em", "Entregue em", "Status", "Tentativas", "Próximo Reenvio", "Erro"],
      ];

      // Adicionar dados das mensagens
      shippingData.forEach(item => {
        excelData.push([
          item.jobId || '',
          item.number || '',
          item.message || '',
          item.sentAt ? formatCompanyDateTime(item.sentAt, companyTimezone) : (item.createdAt ? formatCompanyDateTime(item.createdAt, companyTimezone) : ''),
          item.deliveredAt ? formatCompanyDateTime(item.deliveredAt, companyTimezone) : '',
          normalizeShippingStatus(item),
          item.attempts || 0,
          item.nextRetryAt ? formatCompanyDateTime(item.nextRetryAt, companyTimezone) : '',
          item.errorMessage || ''
        ]);
      });

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Configurar larguras das colunas
      ws['!cols'] = [
        { wch: 15 }, // ID
        { wch: 20 }, // Destinatário
        { wch: 50 }, // Mensagem
        { wch: 20 }, // Enviado em
        { wch: 20 }, // Entregue em
        { wch: 18 }, // Status
        { wch: 12 }, // Tentativas
        { wch: 24 }, // Próximo Reenvio
        { wch: 40 }  // Erro
      ];

      // Mesclar células do cabeçalho
      ws['!merges'] = [
        { s: { c: 0, r: 0 }, e: { c: 8, r: 0 } }, // Título
        { s: { c: 0, r: 1 }, e: { c: 8, r: 1 } }, // Nome da campanha
        { s: { c: 0, r: 2 }, e: { c: 8, r: 2 } }  // Data de geração
      ];

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Campanha");

      // Gerar nome do arquivo
      const fileName = `relatorio_campanha_${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_${formatCompanyDateTime(new Date().toISOString(), companyTimezone).slice(0, 10).replace(/\//g, '-')}.xlsx`;

      // Fazer download
      XLSX.writeFile(wb, fileName);
      
      toast.success(`Relatório Excel gerado com sucesso! ${shippingData.length} registros exportados.`);
      
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/campaigns')}
              startIcon={<ArrowBackIcon />}
            >
              {i18n.t("campaignReport.backButton")}
            </Button>
            <Title>{i18n.t("campaignReport.title")} {campaign.name || i18n.t("campaignReport.campaign")}</Title>
          </Grid>
        </Grid>
      </MainHeader>
      
      {/* Card com resumo e status */}
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {campaign.name || i18n.t("campaignReport.campaign")}
          </Typography>
          <Chip 
            label={formatStatus(campaign.status)} 
            color={campaign.status === "FINALIZADA" ? "primary" : "default"}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
          {campaign.status === "SUSPENSA_CONEXAO" && (
            <Typography variant="body2" color="textSecondary">
              Aguardando reconexão. O envio será reprogramado automaticamente quando a conexão estiver disponível.
            </Typography>
          )}
          {campaign.statusReason && (
            <Typography variant="body2" color="textSecondary">
              Motivo: {campaign.statusReasonDetail || campaign.statusReason}
              {campaign.nextActionAt ? ` | Próxima ação: ${datetimeToClient(campaign.nextActionAt)}` : ""}
            </Typography>
          )}
        </Box>
        
        <Grid container spacing={3} className={classes.summaryCards}>
          {/* Card de Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Status de Entrega
                </Typography>
                <Box className={classes.pieChartContainer}>
                  <Doughnut data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Cards com estatísticas principais */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Enviadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {uniqueNumbers}
                </Typography>
                <Typography color="textSecondary">
                  Destinatários Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <CheckCircleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Entregues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} className={classes.summaryCards}>
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <ScheduleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {operationalStats.pendingMessages || 0}
                </Typography>
                <Typography color="textSecondary">
                  Aguardando Operação
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <WhatsAppIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {operationalStats.waitingConnection || 0}
                </Typography>
                <Typography color="textSecondary">
                  Aguardando Conexão
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <ScheduleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {operationalStats.waitingWarmup || 0}
                </Typography>
                <Typography color="textSecondary">
                  Aguardando Warmup
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <ErrorIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {operationalStats.permanentFailures || failedMessages || 0}
                </Typography>
                <Typography color="textSecondary">
                  Falhas Permanentes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Barra de progresso */}
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">{i18n.t("campaignReport.deliver")}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {delivered} de {totalMessagesSent}
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            style={{ height: 10, borderRadius: 5, margin: "8px 0" }}
            value={(delivered / (totalMessagesSent || 1)) * 100}
          />
        </Box>
        
        {/* Informações adicionais da campanha */}
        <Grid container spacing={3}>
          {campaign.whatsappId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <WhatsAppIcon />
                    </Avatar>
                  }
                  title={i18n.t("campaignReport.whatsappConnection")}
                  subheader={campaign.whatsapp?.name || i18n.t("campaignReport.notSpecified")}
                />
              </Card>
            </Grid>
          )}
          
          {campaign.tagListId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title={i18n.t("campaignReport.usedTag")}
                  subheader={`ID: ${campaign.tagListId}`}
                />
              </Card>
            </Grid>
          )}
          
          {campaign.contactListId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title={i18n.t("campaignReport.contactLists")}
                  subheader={campaign.contactList?.name || i18n.t("campaignReport.notSpecified")}
                />
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title={i18n.t("campaignReport.schedule")}
                subheader={datetimeToClient(campaign.scheduledAt) || i18n.t("campaignReport.notScheduled")}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title={i18n.t("campaignReport.conclusion")}
                subheader={datetimeToClient(campaign.completedAt) || i18n.t("campaignReport.notCompleted")}
              />
            </Card>
          </Grid>
        </Grid>
        
        {/* Seção de detalhes de mensagens */}
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <MessageIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              {i18n.t("campaignReport.messageDetails")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              style={{
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              {i18n.t("campaignReport.exportExcel")}
            </Button>
          </Box>
          <Divider />
          
          <TableContainer className={classes.tableContainer}>
            <Table aria-label={i18n.t("campaignReport.messagesTableAria")}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.recipient")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.message")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.sentAt")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.deliveredAt")}</TableCell>
                  <TableCell>{i18n.t("common.statusLabel")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.attemptsNextRetry")}</TableCell>
                  <TableCell>Diagnóstico</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messageRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.jobId}</TableCell>
                    <TableCell>
                      <Tooltip title={row.number}>
                        <span>{formatPhoneNumber(row.number)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.fullMessage}>
                        <span>{row.message}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.sentAt || '-'}</TableCell>
                    <TableCell>{row.deliveredAt || '-'}</TableCell>
                    <TableCell>{getMessageStatusChip(row.status)}</TableCell>
                    <TableCell>
                      <Tooltip title={row.failureReason || row.errorMessage || row.statusReason || ""}>
                        <span>{formatRetryInfo(row.attempts, row.nextRetryAt, row.status)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {(row.errorMessage || row.failureReason || row.failureDetails || row.statusReason) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedFailure(row)}
                        >
                          Ver
                        </Button>
                      ) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
                {messageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {loading ? i18n.t("campaignReport.loading") : i18n.t("campaignReport.noMessagesFound")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalMessagesSent}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => {
                setPage(newPage);
                // Calcular qual página de dados carregar do servidor
                const serverPage = newPage + 1;
                loadMoreShipping(serverPage, rowsPerPage);
              }}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        </Box>
      </Paper>
      <Dialog
        open={Boolean(selectedFailure)}
        onClose={() => setSelectedFailure(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Diagnóstico do envio</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Destinatário: {selectedFailure?.number || "-"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Status: {shippingStatusLabel[selectedFailure?.status] || selectedFailure?.status || "-"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Categoria: {selectedFailure?.failureCategory || "-"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Motivo: {selectedFailure?.failureReason || selectedFailure?.errorMessage || selectedFailure?.statusReason || "-"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Próximo retry: {selectedFailure?.nextRetryAt ? datetimeToClient(selectedFailure.nextRetryAt) : "-"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Worker: {selectedFailure?.workerName || "-"}
          </Typography>
          {selectedFailure?.failureDetails && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Detalhes
              </Typography>
              <Box component="pre" style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                {parseFailureDetails(selectedFailure.failureDetails)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedFailure(null)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default CampaignReport;
